import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import {
  ArrowLeft,
  Camera,
  FileText,
  Loader2,
  Radio,
  Send,
  Sparkles,
  Upload,
  Wrench,
} from "lucide-react";
import { ConsoleShell } from "@/components/ConsoleShell";
import { useVehicles } from "@/context/VehicleContext";
import { displayVehicleName } from "@/contracts/vehicle";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND = process.env.REACT_APP_BACKEND_URL;

const TABS = [
  "overview",
  "health",
  "maintenance",
  "timeline",
  "recalls",
  "parts",
  "marketplace",
  "shops",
  "bookings",
  "ai",
  "reports",
  "history",
];

const AGENT_CARDS = [
  { intent: "diagnosis", title: "Diagnosis", body: "Symptoms, causes, follow-ups" },
  { intent: "maintenance", title: "Maintenance", body: "Schedules from mileage" },
  { intent: "recall", title: "Recalls", body: "NHTSA safety campaigns" },
  { intent: "repair", title: "Repair Plan", body: "Severity, cost, steps" },
  { intent: "parts", title: "Parts", body: "OEM & aftermarket picks" },
  { intent: "marketplace", title: "Marketplace", body: "Stock, quotes, part requests" },
  { intent: "shop", title: "Service Centers", body: "Nearby rated shops" },
  { intent: "booking", title: "Booking", body: "Appointments & reminders" },
  { intent: "emergency", title: "Emergency", body: "Unsafe-condition protocol" },
  { intent: "vision", title: "Vision", body: "Photo walkaround analysis" },
  { intent: "health", title: "Health", body: "Systems scoreboard" },
  { intent: "report", title: "Reports", body: "PDF for you or a mechanic" },
];

export default function VehicleDashboard() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { setSelectedId, refresh } = useVehicles();
  const [tab, setTab] = useState("overview");
  const [vehicle, setVehicle] = useState(null);
  const [health, setHealth] = useState(null);
  const [maintenance, setMaintenance] = useState([]);
  const [timeline, setTimeline] = useState([]);
  const [recalls, setRecalls] = useState(null);
  const [parts, setParts] = useState(null);
  const [marketListings, setMarketListings] = useState([]);
  const [marketBusy, setMarketBusy] = useState(false);
  const [shops, setShops] = useState([]);
  const [shopMeta, setShopMeta] = useState({ city: "", source: "", lat: null, lng: null });
  const [geoBusy, setGeoBusy] = useState(false);
  const [geoError, setGeoError] = useState("");
  const [geoAutoAttempted, setGeoAutoAttempted] = useState(false);
  const [appointments, setAppointments] = useState([]);
  const [reports, setReports] = useState([]);
  const [chats, setChats] = useState([]);
  const [repairs, setRepairs] = useState([]);
  const [loadedTabs, setLoadedTabs] = useState({
    recalls: false,
    shops: false,
    bookings: false,
    reports: false,
    history: false,
    marketplace: false,
  });
  const [loading, setLoading] = useState(true);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [chatBusy, setChatBusy] = useState(false);
  const [activeAgent, setActiveAgent] = useState("");
  const [inspectBusy, setInspectBusy] = useState(false);
  const [inspectReport, setInspectReport] = useState("");
  const fileRef = useRef(null);
  const bottomRef = useRef(null);
  const briefRef = useRef(false);

  const load = useCallback(async ({ resetLazy = false } = {}) => {
    setLoading(true);
    try {
      const v = (await axios.get(`${API}/vehicles/${id}`)).data;
      setVehicle(v);
      setSelectedId(id);
      const [h, m, t] = await Promise.all([
        axios.get(`${API}/vehicles/${id}/health`).then((x) => x.data).catch(() => null),
        axios.get(`${API}/vehicles/${id}/maintenance`).then((x) => x.data).catch(() => []),
        axios.get(`${API}/vehicles/${id}/timeline`).then((x) => x.data).catch(() => []),
      ]);
      setHealth(h);
      setMaintenance(m);
      setTimeline(t);
      if (resetLazy) {
        setRecalls(null);
        setParts(null);
        setMarketListings([]);
        setShops([]);
        setShopMeta({ city: "", source: "", lat: null, lng: null, location_label: "" });
        setGeoAutoAttempted(false);
        setAppointments([]);
        setReports([]);
        setChats([]);
        setRepairs([]);
        setLoadedTabs({
          recalls: false,
          shops: false,
          bookings: false,
          reports: false,
          history: false,
          marketplace: false,
        });
      }
    } catch {
      setVehicle(null);
    } finally {
      setLoading(false);
    }
  }, [id, setSelectedId]);

  useEffect(() => {
    briefRef.current = false;
    load({ resetLazy: true });
  }, [load]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const streamAsk = async (message = "", intent = null) => {
    if (chatBusy) return;
    setTab("ai");
    const q = (message || "").trim();
    setMessages((m) => [
      ...m,
      ...(q ? [{ role: "user", content: q }] : []),
      { role: "assistant", content: "" },
    ]);
    setChatBusy(true);
    setActiveAgent(intent || "orchestrator");
    try {
      const res = await fetch(`${API}/ai/orchestrate`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ vehicle_id: id, message: q, intent }),
      });
      if (!res.ok || !res.body) throw new Error(`HTTP ${res.status}`);
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const frames = buf.split("\n\n");
        buf = frames.pop() || "";
        for (const frame of frames) {
          const lines = frame.split("\n");
          let event = "message";
          const dataParts = [];
          for (const line of lines) {
            if (line.startsWith("event: ")) event = line.slice(7).trim();
            else if (line.startsWith("data: ")) dataParts.push(line.slice(6));
          }
          const data = dataParts.join("\n").replace(/\\n/g, "\n");
          if (event === "agent") {
            setActiveAgent(data.split("|")[0]);
            setMessages((m) => {
              const next = [...m];
              next.splice(next.length - 1, 0, { role: "tool", content: data.split("|")[0] });
              return next;
            });
          } else if (event === "error") {
            setMessages((m) => {
              const next = [...m];
              const last = next[next.length - 1];
              next[next.length - 1] = {
                ...last,
                content: last.content || data,
                error: true,
              };
              return next;
            });
          } else if (data !== "[DONE]") {
            setMessages((m) => {
              const next = [...m];
              const last = next[next.length - 1];
              next[next.length - 1] = { ...last, content: last.content + data };
              return next;
            });
          }
        }
      }
      await load();
    } catch (e) {
      setMessages((m) => {
        const next = [...m];
        const last = next[next.length - 1];
        next[next.length - 1] = {
          ...last,
          content: last.content || "Agent stream failed.",
          error: true,
        };
        return next;
      });
    } finally {
      setChatBusy(false);
    }
  };

  useEffect(() => {
    if (vehicle && !briefRef.current && tab === "overview") {
      briefRef.current = true;
      // soft-load AI brief once when opening dashboard
    }
  }, [vehicle, tab]);

  const loadParts = async () => {
    const { data } = await axios.post(`${API}/vehicles/${id}/parts/recommend`, {
      issue: "general maintenance",
    });
    setParts(data);
  };

  const loadMarketplace = useCallback(async () => {
    const data = await axios
      .get(`${API}/marketplace/listings`, { params: { vehicle_id: id, limit: 16 } })
      .then((x) => x.data)
      .catch(() => ({ listings: [] }));
    setMarketListings(data.listings || []);
  }, [id]);

  const loadRecalls = useCallback(async () => {
    const data = await axios.get(`${API}/vehicles/${id}/recalls`).then((x) => x.data).catch(() => null);
    setRecalls(data);
  }, [id]);

  const loadBookings = useCallback(async () => {
    const data = await axios.get(`${API}/appointments`).then((x) => x.data).catch(() => []);
    setAppointments((data || []).filter((x) => x.vehicle_id === id));
  }, [id]);

  const loadReports = useCallback(async () => {
    const data = await axios.get(`${API}/vehicles/${id}/reports`).then((x) => x.data).catch(() => []);
    setReports(data || []);
  }, [id]);

  const loadHistory = useCallback(async () => {
    const [ch, rp] = await Promise.all([
      axios.get(`${API}/vehicles/${id}/chats`).then((x) => x.data).catch(() => []),
      axios.get(`${API}/vehicles/${id}/repairs`).then((x) => x.data).catch(() => []),
    ]);
    setChats(ch || []);
    setRepairs(rp || []);
  }, [id]);

  const loadDefaultShops = useCallback(async () => {
    const s = await axios.get(`${API}/shops/nearby`).then((x) => x.data).catch(() => ({ shops: [] }));
    const shopPayload = Array.isArray(s) ? { shops: s } : s || {};
    setShops(shopPayload.shops || []);
    setShopMeta({
      city: shopPayload.city || "",
      source: shopPayload.source || "",
      lat: shopPayload.lat ?? null,
      lng: shopPayload.lng ?? null,
      location_label: shopPayload.location_label || "",
    });
  }, []);

  const genMaintenance = async () => {
    const { data } = await axios.post(`${API}/vehicles/${id}/maintenance/generate`);
    setMaintenance(data);
  };

  const completeMaint = async (itemId) => {
    await axios.patch(`${API}/vehicles/${id}/maintenance/${itemId}`, { status: "completed" });
    await load();
  };

  const bookShop = async (shop) => {
    const when = new Date(Date.now() + 3 * 86400000).toISOString();
    await axios.post(`${API}/vehicles/${id}/appointments`, {
      shop_id: shop.id,
      shop_name: shop.name,
      scheduled_at: when,
      service: "Diagnostic inspection",
      notes: "Booked from Vehicle Dashboard",
    });
    await load();
    setTab("bookings");
  };

  const loadShopsNear = async (lat, lng, persist = true) => {
    const { data } = await axios.get(`${API}/shops/nearby`, {
      params: { lat, lng, radius_km: 15, persist },
    });
    setShops(data.shops || []);
    setShopMeta({
      city: data.city || "",
      source: data.source || "",
      lat: data.lat ?? lat,
      lng: data.lng ?? lng,
      location_label: data.location_label || "",
    });
  };

  const requestMyLocation = useCallback(() => {
    setGeoError("");
    if (!navigator.geolocation) {
      setGeoError("Geolocation is not supported in this browser.");
      return;
    }
    setGeoBusy(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        try {
          await loadShopsNear(pos.coords.latitude, pos.coords.longitude, true);
        } catch (e) {
          setGeoError(e.response?.data?.detail || "Could not load nearby shops.");
        } finally {
          setGeoBusy(false);
        }
      },
      (err) => {
        setGeoBusy(false);
        setGeoError(err.message || "Location permission denied.");
      },
      { enableHighAccuracy: true, timeout: 20000, maximumAge: 60000 }
    );
  }, [loadShopsNear]);

  useEffect(() => {
    if (!vehicle) return;

    const loadForTab = async () => {
      if (tab === "recalls" && !loadedTabs.recalls) {
        await loadRecalls();
        setLoadedTabs((prev) => ({ ...prev, recalls: true }));
      } else if (tab === "shops" && !loadedTabs.shops) {
        await loadDefaultShops();
        setLoadedTabs((prev) => ({ ...prev, shops: true }));
      } else if (tab === "bookings" && !loadedTabs.bookings) {
        await loadBookings();
        setLoadedTabs((prev) => ({ ...prev, bookings: true }));
      } else if (tab === "reports" && !loadedTabs.reports) {
        await loadReports();
        setLoadedTabs((prev) => ({ ...prev, reports: true }));
      } else if (tab === "history" && !loadedTabs.history) {
        await loadHistory();
        setLoadedTabs((prev) => ({ ...prev, history: true }));
      } else if (tab === "marketplace" && !loadedTabs.marketplace) {
        await loadMarketplace();
        setLoadedTabs((prev) => ({ ...prev, marketplace: true }));
      } else if (tab === "parts" && !parts) {
        await loadParts();
      }
    };

    loadForTab().catch(() => {
      // Keep page interactive even if a lazy tab fetch fails.
    });
  }, [tab, vehicle, loadedTabs, parts, loadRecalls, loadDefaultShops, loadBookings, loadReports, loadHistory, loadMarketplace]);

  useEffect(() => {
    if (tab !== "shops") return;
    if (geoAutoAttempted) return;

    const lat = shopMeta.lat != null ? Number(shopMeta.lat) : null;
    const lng = shopMeta.lng != null ? Number(shopMeta.lng) : null;
    const isDefaultSriLanka =
      lat != null &&
      lng != null &&
      Math.abs(lat - 6.9271) < 0.01 &&
      Math.abs(lng - 79.8612) < 0.01;

    // Only auto-prompt when the app is still using the initial Sri Lanka fallback coords.
    if (lat == null || lng == null || isDefaultSriLanka) {
      setGeoAutoAttempted(true);
      requestMyLocation();
    }
  }, [tab, geoAutoAttempted, shopMeta.lat, shopMeta.lng, requestMyLocation]);

  const requestMarketPart = async (listing) => {
    setMarketBusy(true);
    try {
      await axios.post(`${API}/marketplace/requests`, {
        listing_id: listing.id,
        qty: 1,
        vehicle_id: id,
        note: `Request from vehicle OS for ${listing.sku}`,
      });
      streamAsk(`Do you have ${listing.sku} ${listing.name} in stock for this vehicle? Please request it if needed.`, "marketplace");
    } catch {
      streamAsk(`I want ${listing.name} (${listing.sku}) for this vehicle. Do you have it?`, "marketplace");
    } finally {
      setMarketBusy(false);
    }
  };

  const makeReport = async (type) => {
    const { data } = await axios.post(`${API}/vehicles/${id}/reports/${type}`);
    setReports((r) => [data, ...r]);
    window.open(`${BACKEND}${data.path}`, "_blank");
  };

  const onInspect = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setInspectBusy(true);
    setInspectReport("");
    try {
      const form = new FormData();
      form.append("image", f);
      form.append("kind", "inspect");
      form.append("note", "Dashboard upload");
      const { data } = await axios.post(`${API}/vehicles/${id}/images`, form);
      setInspectReport(data.report || "");
      await load();
    } catch (err) {
      setInspectReport(err.response?.data?.detail || "Inspection failed");
    } finally {
      setInspectBusy(false);
    }
  };

  if (loading) {
    return (
      <ConsoleShell title="vehicle">
        <div className="py-32 flex justify-center font-mono-tech text-[11px] uppercase tracking-widest text-cyan-electric">
          <Loader2 className="h-4 w-4 animate-spin mr-2" /> loading vehicle os...
        </div>
      </ConsoleShell>
    );
  }

  if (!vehicle) {
    return (
      <ConsoleShell title="vehicle">
        <div className="py-32 text-center">
          <div className="font-display text-3xl">Vehicle not found</div>
          <Link to="/console/garage" className="mt-6 inline-block text-cyan-electric font-mono-tech text-[11px] uppercase">
            back to garage
          </Link>
        </div>
      </ConsoleShell>
    );
  }

  return (
    <ConsoleShell title="vehicle os">
      <div className="mx-auto max-w-[1500px] px-6 lg:px-10 pt-8 pb-24">
        <Link
          to="/console/garage"
          className="inline-flex items-center gap-2 font-mono-tech text-[10px] uppercase tracking-widest text-white/50 hover:text-cyan-electric"
        >
          <ArrowLeft className="h-3.5 w-3.5" /> garage
        </Link>

        <div className="mt-6 grid grid-cols-1 lg:grid-cols-12 gap-8">
          <div className="lg:col-span-4">
            <div className="border border-white/10 bg-obsidian/70 overflow-hidden">
              <div className="aspect-[16/10] bg-black relative">
                {vehicle.image_url ? (
                  <img src={vehicle.image_url} alt="" referrerPolicy="no-referrer" className="absolute inset-0 w-full h-full object-cover" />
                ) : (
                  <div className="absolute inset-0 flex items-center justify-center font-display text-6xl text-white/20">
                    {vehicle.brand?.[0]}
                  </div>
                )}
              </div>
              <div className="p-5">
                <div className="font-mono-tech text-[10px] uppercase tracking-widest text-white/40">
                  {vehicle.year} · {vehicle.trim || "trim n/a"}
                </div>
                <h1 className="mt-1 font-display text-3xl tracking-tighter">{displayVehicleName(vehicle)}</h1>
                <div className="mt-4 grid grid-cols-2 gap-3 font-mono-tech text-[10px] uppercase tracking-widest text-white/55">
                  <div>Mileage · {Number(vehicle.mileage || 0).toLocaleString()}</div>
                  <div>Health · {health?.score ?? vehicle.health_score ?? "-"}</div>
                  <div className="col-span-2 truncate">VIN · {vehicle.vin || "not on file"}</div>
                  {vehicle.tecdoc_ktype && (
                    <div className="col-span-2 flex flex-wrap gap-2 pt-1">
                      <span className="border border-cyan-electric/40 text-cyan-electric px-2 py-0.5">
                        TecDoc KType {vehicle.tecdoc_ktype}
                      </span>
                      <span className="border border-white/20 text-white/50 px-2 py-0.5">
                        {vehicle.tecalliance_source || "tecalliance"} dataset
                      </span>
                    </div>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => streamAsk("", "diagnosis")}
                  className="mt-5 w-full h-11 bg-mclaren text-void font-mono-tech text-[11px] uppercase tracking-widest"
                >
                  run vehicle brief
                </button>
              </div>
            </div>

            <div className="mt-4 border border-white/10 bg-obsidian/70 p-4">
              <div className="flex items-center gap-2 font-mono-tech text-[10px] uppercase tracking-widest text-white/50 mb-3">
                <Camera className="h-3.5 w-3.5 text-cyan-electric" /> Vision Inspector
              </div>
              <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onInspect} />
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                disabled={inspectBusy}
                className="w-full h-20 border border-dashed border-white/20 hover:border-cyan-electric flex flex-col items-center justify-center gap-2 text-white/50"
              >
                {inspectBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                <span className="font-mono-tech text-[10px] uppercase tracking-widest">upload photo</span>
              </button>
              {inspectReport && (
                <pre className="mt-3 text-xs text-white/75 whitespace-pre-wrap max-h-48 overflow-y-auto">{inspectReport}</pre>
              )}
            </div>
          </div>

          <div className="lg:col-span-8">
            <div className="flex flex-wrap gap-2 mb-6">
              {TABS.map((t) => (
                <button
                  key={t}
                  type="button"
                  onClick={() => {
                    setTab(t);
                    if (t === "parts" && !parts) loadParts();
                  }}
                  className={`font-mono-tech text-[9px] uppercase tracking-widest px-3 h-8 border ${
                    tab === t
                      ? "border-cyan-electric text-cyan-electric"
                      : "border-white/10 text-white/50 hover:border-white/30"
                  }`}
                >
                  {t}
                </button>
              ))}
            </div>

            {tab === "overview" && (
              <div>
                <h2 className="font-display text-2xl tracking-tighter mb-4">AI Automotive OS agents</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {AGENT_CARDS.map((a) => (
                    <button
                      key={a.intent}
                      type="button"
                      onClick={() => streamAsk(`Help with ${a.title.toLowerCase()} for this vehicle.`, a.intent)}
                      className="text-left border border-white/10 bg-black/30 p-4 hover:border-cyan-electric/50"
                    >
                      <div className="font-mono-tech text-[10px] uppercase tracking-widest text-cyan-electric">
                        {a.title}
                      </div>
                      <div className="mt-2 text-sm text-white/60">{a.body}</div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {tab === "health" && health && (
              <div className="border border-white/10 bg-obsidian/70 p-6">
                <div className="font-display text-5xl tracking-tighter text-cyan-electric">
                  {health.score}
                  <span className="text-xl text-white/40 ml-2">{health.grade}</span>
                </div>
                <p className="mt-2 text-white/55 text-sm">{health.summary}</p>
                <div className="mt-6 grid sm:grid-cols-2 gap-3">
                  {Object.entries(health.systems || {}).map(([k, v]) => (
                    <div key={k} className="border border-white/10 p-4">
                      <div className="font-mono-tech text-[10px] uppercase tracking-widest text-white/40">{k}</div>
                      <div className="mt-1 font-display text-2xl">{v.score}</div>
                      <div className="text-xs text-white/55 mt-1">{v.note}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {tab === "maintenance" && (
              <div>
                <div className="flex justify-between mb-4">
                  <h2 className="font-display text-2xl tracking-tighter">Maintenance</h2>
                  <button
                    type="button"
                    onClick={genMaintenance}
                    className="h-9 px-3 border border-cyan-electric/50 text-cyan-electric font-mono-tech text-[10px] uppercase"
                  >
                    regenerate
                  </button>
                </div>
                <div className="space-y-2">
                  {maintenance.map((m) => (
                    <div key={m.id} className="border border-white/10 p-4 flex items-center justify-between gap-3">
                      <div>
                        <div className="font-display text-lg tracking-tight">{m.title}</div>
                        <div className="font-mono-tech text-[10px] uppercase text-white/40">
                          {m.status} · due {m.due_mileage ? `${m.due_mileage.toLocaleString()} mi` : m.due_date || "-"}
                        </div>
                      </div>
                      {m.status !== "completed" && (
                        <button
                          type="button"
                          onClick={() => completeMaint(m.id)}
                          className="h-9 px-3 border border-white/20 text-[10px] font-mono-tech uppercase"
                        >
                          complete
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {tab === "timeline" && (
              <div className="space-y-3">
                {timeline.map((e) => (
                  <div key={e.id} className="border-l border-cyan-electric/40 pl-4 py-2">
                    <div className="font-mono-tech text-[10px] uppercase tracking-widest text-cyan-electric">
                      {e.type} · {e.created_at?.slice(0, 19)}
                    </div>
                    <div className="font-display text-lg tracking-tight">{e.title}</div>
                    <div className="text-sm text-white/50">{e.detail}</div>
                  </div>
                ))}
                {timeline.length === 0 && <div className="text-white/40">No timeline events yet.</div>}
              </div>
            )}

            {tab === "recalls" && (
              <div>
                <h2 className="font-display text-2xl tracking-tighter mb-2">
                  NHTSA recalls · {recalls?.count ?? 0}
                </h2>
                <div className="space-y-3 mt-4">
                  {(recalls?.recalls || []).map((r, i) => (
                    <div key={i} className="border border-white/10 p-4">
                      <div className="font-mono-tech text-[10px] uppercase text-mclaren">
                        {r.campaign} · {r.component}
                      </div>
                      <div className="mt-2 text-sm text-white/75">{r.summary}</div>
                      {r.remedy && <div className="mt-2 text-xs text-white/45">Remedy: {r.remedy}</div>}
                    </div>
                  ))}
                  {!recalls?.count && (
                    <div className="text-white/50 text-sm">No open recalls for this make/model/year.</div>
                  )}
                </div>
              </div>
            )}

            {tab === "parts" && (
              <div>
                <div className="flex items-end justify-between gap-4 mb-4">
                  <h2 className="font-display text-2xl tracking-tighter">Parts recommendations</h2>
                  <Link
                    to="/console/marketplace"
                    className="font-mono-tech text-[10px] uppercase tracking-widest text-cyan-electric"
                  >
                    open marketplace
                  </Link>
                </div>
                <div className="space-y-2">
                  {(parts?.parts || []).map((p, i) => (
                    <div key={i} className="border border-white/10 p-4 flex justify-between gap-3">
                      <div>
                        <div className="font-display text-lg">{p.name}</div>
                        <div className="font-mono-tech text-[10px] uppercase text-white/40">
                          {p.type} · {p.availability} · {p.compatibility}
                        </div>
                      </div>
                      <div className="text-cyan-electric font-display text-xl">${p.est_price_usd}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {tab === "marketplace" && (
              <div>
                <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-4">
                  <div>
                    <h2 className="font-display text-2xl tracking-tighter">Marketplace for this vehicle</h2>
                    <p className="mt-1 text-sm text-white/45">
                      Stock matched to {displayVehicleName(vehicle)}. Request a part or ask the agent if we have it.
                    </p>
                  </div>
                  <Link
                    to="/console/marketplace"
                    className="h-10 px-4 border border-cyan-electric/50 text-cyan-electric font-mono-tech text-[10px] uppercase tracking-widest inline-flex items-center"
                  >
                    full catalog
                  </Link>
                </div>
                {marketListings.length === 0 && (
                  <div className="text-white/50 text-sm mb-4">No matched listings yet. Open the full catalog or ask the agent.</div>
                )}
                <div className="space-y-2">
                  {marketListings.map((p) => (
                    <div key={p.id || p.sku} className="border border-white/10 p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                      <div>
                        <div className="font-display text-lg">{p.name}</div>
                        <div className="font-mono-tech text-[10px] uppercase text-white/40">
                          {p.sku} · {p.availability_label} · {p.category}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="text-cyan-electric font-display text-xl mr-2">
                          {p.price_usd > 0 ? `$${p.price_usd}` : "quote"}
                        </div>
                        <button
                          type="button"
                          disabled={marketBusy}
                          onClick={() => requestMarketPart(p)}
                          className="h-10 px-3 bg-cyan-electric text-void font-mono-tech text-[10px] uppercase tracking-widest"
                        >
                          request
                        </button>
                        <button
                          type="button"
                          onClick={() =>
                            streamAsk(`Do you have ${p.sku} ${p.name} in stock for this registered vehicle?`, "marketplace")
                          }
                          className="h-10 px-3 border border-white/20 font-mono-tech text-[10px] uppercase tracking-widest"
                        >
                          ask ai
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {tab === "shops" && (
              <div>
                <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-4">
                  <div>
                    <h2 className="font-display text-2xl tracking-tighter">Service centers</h2>
                    <p className="mt-1 font-mono-tech text-[10px] uppercase tracking-widest text-white/40">
                      {shopMeta.city || "Location"} ·{" "}
                      {shopMeta.source === "openstreetmap" ? "live OpenStreetMap" : "fallback directory"}
                      {shopMeta.lat != null && (
                        <> · {Number(shopMeta.lat).toFixed(4)}, {Number(shopMeta.lng).toFixed(4)}</>
                      )}
                    </p>
                    {shopMeta.location_label && (
                      <p className="mt-1 text-xs text-white/45 max-w-xl">{shopMeta.location_label}</p>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={requestMyLocation}
                    disabled={geoBusy}
                    className="h-10 px-4 bg-cyan-electric text-void font-mono-tech text-[10px] uppercase tracking-widest inline-flex items-center gap-2 disabled:opacity-60"
                  >
                    {geoBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
                    use my location
                  </button>
                </div>
                {geoError && (
                  <div className="mb-4 text-sm text-red-300/90 border border-red-400/20 p-3">{geoError}</div>
                )}
                {shops.length === 0 && (
                  <div className="text-white/50 text-sm">No shops found. Tap Use my location or widen search.</div>
                )}
                <div className="space-y-2">
                  {shops.map((s) => (
                    <div key={s.id} className="border border-white/10 p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                      <div>
                        <div className="font-display text-lg">{s.name}</div>
                        <div className="font-mono-tech text-[10px] uppercase text-white/40">
                          {s.distance_km ?? s.distance_mi} km · ★{s.rating} · {s.certified ? "brand/operator" : "indie"} ·{" "}
                          {(s.specialty || []).join(", ")}
                          {s.source === "openstreetmap" ? " · osm" : ""}
                        </div>
                        <div className="text-xs text-white/45 mt-1">
                          {s.address}
                          {s.city ? `, ${s.city}` : ""}
                          {s.phone ? ` · ${s.phone}` : ""}
                        </div>
                        {(s.google_maps_url || s.maps_url) && (
                          <a
                            href={s.google_maps_url || s.maps_url}
                            target="_blank"
                            rel="noreferrer"
                            className="inline-block mt-2 font-mono-tech text-[10px] uppercase tracking-widest text-cyan-electric hover:underline"
                          >
                            open in maps
                          </a>
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={() => bookShop(s)}
                        className="h-10 px-4 bg-cyan-electric text-void font-mono-tech text-[10px] uppercase tracking-widest"
                      >
                        book
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {tab === "bookings" && (
              <div>
                <h2 className="font-display text-2xl tracking-tighter mb-4">Appointments</h2>
                {appointments.length === 0 && <div className="text-white/50">No bookings yet.</div>}
                <div className="space-y-2">
                  {appointments.map((a) => (
                    <div key={a.id} className="border border-white/10 p-4">
                      <div className="font-display text-lg">{a.shop_name}</div>
                      <div className="font-mono-tech text-[10px] uppercase text-white/40">
                        {a.service} · {a.scheduled_at} · {a.status}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {tab === "ai" && (
              <div className="border border-white/10 bg-obsidian/70 flex flex-col h-[640px]">
                <div className="flex items-center justify-between px-4 py-3 border-b border-white/10 bg-black/40">
                  <div className="flex items-center gap-2 font-mono-tech text-[10px] uppercase tracking-widest text-white/60">
                    <Sparkles className="h-3.5 w-3.5 text-cyan-electric" />
                    Orchestrator · Nova 2 Lite · locked to {displayVehicleName(vehicle)}
                  </div>
                  <span className="font-mono-tech text-[10px] uppercase text-cyan-electric">
                    {activeAgent || "idle"}
                  </span>
                </div>
                <div className="flex-1 overflow-y-auto p-4 space-y-3">
                  {messages.map((m, i) =>
                    m.role === "tool" ? (
                      <div
                        key={i}
                        className="inline-flex items-center gap-2 font-mono-tech text-[10px] uppercase tracking-widest text-cyan-electric border border-cyan-electric/30 px-3 py-1.5"
                      >
                        <Wrench className="h-3 w-3" /> agent · {m.content}
                      </div>
                    ) : (
                      <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                        <div
                          className={`max-w-[90%] px-4 py-3 text-sm whitespace-pre-wrap ${
                            m.role === "user"
                              ? "bg-mclaren/15 border border-mclaren/40"
                              : m.error
                              ? "bg-mclaren/5 border border-mclaren/30 text-mclaren"
                              : "bg-black/40 border border-white/10"
                          }`}
                        >
                          {m.content || (chatBusy ? "…" : "")}
                        </div>
                      </div>
                    ),
                  )}
                  <div ref={bottomRef} />
                </div>
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    const q = input.trim();
                    if (!q || chatBusy) return;
                    setInput("");
                    streamAsk(q);
                  }}
                  className="border-t border-white/10 p-3 flex gap-2"
                >
                  <div className="relative flex-1">
                    <Radio className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-white/30" />
                    <input
                      value={input}
                      onChange={(e) => setInput(e.target.value)}
                      disabled={chatBusy}
                      placeholder="Ask about THIS car, or if we have a part in the marketplace..."
                      className="w-full bg-black/50 border border-white/15 pl-9 pr-3 h-11 font-mono-tech text-sm"
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={chatBusy || !input.trim()}
                    className="h-11 px-4 bg-mclaren text-void disabled:opacity-40"
                  >
                    {chatBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                  </button>
                </form>
              </div>
            )}

            {tab === "reports" && (
              <div>
                <h2 className="font-display text-2xl tracking-tighter mb-4">PDF reports</h2>
                <div className="flex flex-wrap gap-2 mb-6">
                  {["health", "diagnosis", "mechanic"].map((t) => (
                    <button
                      key={t}
                      type="button"
                      onClick={() => makeReport(t)}
                      className="inline-flex items-center gap-2 h-10 px-4 border border-cyan-electric/50 text-cyan-electric font-mono-tech text-[10px] uppercase"
                    >
                      <FileText className="h-3.5 w-3.5" /> {t}
                    </button>
                  ))}
                </div>
                <div className="space-y-2">
                  {reports.map((r) => (
                    <a
                      key={r.id}
                      href={`${BACKEND}${r.path}`}
                      target="_blank"
                      rel="noreferrer"
                      className="block border border-white/10 p-4 hover:border-cyan-electric"
                    >
                      <div className="font-mono-tech text-[10px] uppercase text-cyan-electric">{r.type}</div>
                      <div className="text-sm text-white/60">{r.created_at}</div>
                    </a>
                  ))}
                </div>
              </div>
            )}

            {tab === "history" && (
              <div className="space-y-6">
                <div>
                  <h2 className="font-display text-2xl tracking-tighter mb-3">Repair history</h2>
                  {repairs.length === 0 && <div className="text-white/40 text-sm">No repairs logged.</div>}
                  {repairs.map((r) => (
                    <div key={r.id} className="border border-white/10 p-4 mb-2">
                      <div className="font-display text-lg">{r.title}</div>
                      <div className="text-sm text-white/50">{r.description}</div>
                    </div>
                  ))}
                </div>
                <div>
                  <h2 className="font-display text-2xl tracking-tighter mb-3">AI sessions</h2>
                  {chats.map((c) => (
                    <div key={c.id} className="border border-white/10 p-4 mb-2">
                      <div className="font-mono-tech text-[10px] uppercase text-cyan-electric">
                        {c.agent || c.intent || "session"} · {c.created_at?.slice(0, 19)}
                      </div>
                      {(c.role_pair || []).map((p, i) => (
                        <div key={i} className="mt-2 text-sm text-white/70">
                          <span className="text-white/40">{p.role}: </span>
                          {String(p.content || "").slice(0, 240)}
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </ConsoleShell>
  );
}
